import React from 'react';
import { useTranslation } from 'react-i18next';
import { hasDepthRun, depthRunSummary, shotCoverage, depthRunVerdict } from '../../depthTrace';
import { DepthTraceChart } from './charts/DepthTraceChart';

// What the depth counter recorded during a run, and whether the survey's shots
// agree with it.
//
// A customer wanted evidence that the tool was physically run down the hole and
// back rather than fabricated at the surface. This is that evidence: the
// counter's own depth-vs-time curve, the figures it accumulated along the way,
// and the cross-check that each shot's depth lies on the curve at that shot's
// timestamp. Two independent records of one run are a much harder thing to fake
// than either alone.
//
// **It renders nothing at all for a survey with no counter**, which is most
// surveys on every tool we ship. Shared by the approval page, the drillhole view
// and the analysis page so the three cannot drift apart, and self-gating so none
// of them needs its own test — the AzimuthDatumBar arrangement.
//
// For that to hold, **callers must render it bare**. Wrapping it in the local
// `<div className="mb-3">` idiom would leave an empty box on every ordinary
// survey, which is the one thing the specification rules out. The panel owns its
// own outer div for exactly that reason.
//
// One verdict is offered and the rest of the figures are left alone: whether the
// shots agree with the counter, as a light, which is what the customer asked for.
// Top speed is still reported bare, because there is no configured limit to
// measure it against and a threshold invented here would be fitted to nothing.
export const DepthRunPanel = ({ survey, height = 300 }) => {
    const { t } = useTranslation();
    if (!hasDepthRun(survey)) return null;

    const summary = depthRunSummary(survey);
    const coverage = shotCoverage(survey);
    const verdict = depthRunVerdict(survey);
    const marksAreShots = coverage.source !== 'depthMarks';
    const markLabel = t(marksAreShots ? 'depthRun.shot' : 'depthRun.depthMark');
    const hasTrace = summary.pointCount > 0;
    const range = Number.isFinite(summary.minMetres) && Number.isFinite(summary.maxMetres)
        ? t('depthRun.rangeValue', { min: summary.minMetres.toFixed(2), max: summary.maxMetres.toFixed(2) })
        : null;

    return (
        <div className="mb-3 border rounded bg-white p-3">
            {/* A plain bordered panel rather than a .card: cards are themed solid
                red app-wide in custom.css, which these figures cannot be read
                against. */}
            <h6 className="mb-2 d-flex align-items-center">
                <span>
                    {t('depthRun.title')}
                    {summary.counterName && <span className="text-muted fw-normal"> · {summary.counterName}</span>}
                </span>
                <Verdict verdict={verdict} />
            </h6>

            <p className="small mb-1">
                <Figure label={t('depthRun.connected')} value={duration(summary.connectedSeconds)} />
                <Figure label={t('depthRun.travelled')} value={metres(summary.distanceMetres)} />
                <Figure label={t('depthRun.range')} value={range} />
                {/* Shown, not judged: there is no configured limit to measure it
                    against, and inventing one now would be fitted to nothing. */}
                <Figure label={t('depthRun.topSpeed')} value={number(summary.maxSpeedMpm, 1, ' m/min')} />
                <Figure label={t('depthRun.link')} value={percent(summary.linkQuality)} />
                <Figure
                    label={t('depthRun.dropouts')}
                    value={summary.gapCount === null ? null
                        : `${summary.gapCount}${summary.gapSeconds ? ` (${duration(summary.gapSeconds)})` : ''}`}
                    last />
            </p>

            {coverage.total > 0 && (
                <p className="small mb-2">
                    <strong>{t(marksAreShots ? 'depthRun.shotsOnCurve' : 'depthRun.marksOnCurve')}</strong>{' '}
                    {t('depthRun.coveredOfTotal', { covered: coverage.covered, total: coverage.total })}
                    {coverage.covered > 0 && <>
                        {' · '}{t('depthRun.residuals', { mean: metres(coverage.meanResidual), worst: metres(coverage.maxResidual) })}
                    </>}
                    {coverage.covered < coverage.total && <>
                        {' · '}
                        <span className="text-muted">
                            {t('depthRun.uncovered', { number: coverage.total - coverage.covered })}
                        </span>
                    </>}
                </p>
            )}

            {hasTrace
                ? <DepthTraceChart trace={survey.DepthTrace} coverage={coverage}
                    markLabel={markLabel} height={height} />
                : <p className="small text-muted fst-italic mb-0">
                    {t('depthRun.noTrace', { number: summary.sampleCount })}
                </p>}
        </div>
    );
};

// The light. Green and red are the customer's ask; the third state is what has to
// sit beside them, because a run where nothing could be compared has produced no
// evidence either way and a green light would claim some. It is deliberately not
// a shade of green or red.
const Verdict = ({ verdict }) => {
    const { t } = useTranslation();
    if (!verdict) return null;
    const { status, checked, total, failing, threshold } = verdict;
    const badge = { pass: 'bg-success', fail: 'bg-danger', unchecked: 'bg-secondary' }[status];
    const label = t({ pass: 'depthRun.pass', fail: 'depthRun.fail', unchecked: 'depthRun.unchecked' }[status]);
    const values = { checked, total, failing, threshold: threshold.toFixed(1) };
    const title = t({
        pass: 'depthRun.passTitle',
        fail: 'depthRun.failTitle',
        unchecked: 'depthRun.uncheckedTitle'
    }[status], values);

    return (
        <span className={`badge ${badge} ms-2 fw-normal`} title={title}>
            {label}
        </span>
    );
};

// One labelled figure in the scalar line. Absent figures are dropped rather than
// shown as a dash: nothing guarantees every scalar is present just because the
// run recorded a sample count, and a row of dashes reads as a broken panel. The
// label carries its own colon, since French puts a space before it.
const Figure = ({ label, value, last }) => (value === null || value === undefined ? null : (
    <span><strong>{label}</strong> {value}{last ? '' : ' · '}</span>
));

// Every formatter below tolerates a missing figure. An unguarded toFixed on an
// absent scalar throws inside render and takes the whole page down with it.
const number = (value, places, suffix = '') =>
    (Number.isFinite(value) ? `${value.toFixed(places)}${suffix}` : null);

const metres = (value) => number(value, 2, ' m');

const percent = (value) => (Number.isFinite(value) ? `${(value * 100).toFixed(0)}%` : null);

const duration = (seconds) => {
    if (!Number.isFinite(seconds)) return null;
    const whole = Math.round(seconds);
    const minutes = Math.floor(whole / 60);
    return minutes > 0 ? `${minutes}m ${whole % 60}s` : `${whole}s`;
};
